// tokenManager.ts
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { ITokenResponse } from "../model/auth";
import { toast } from "react-toastify";
import { LINKS } from "../../../constants/menu";

const tokenStorage = {
  getAccessToken: () =>
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token"),
  getRefreshToken: () =>
    localStorage.getItem("refresh_token") ||
    sessionStorage.getItem("refresh_token"),
  setTokens: ({ data }: ITokenResponse) => {
    localStorage.setItem("access_token", data.access_token);
    localStorage.setItem("refresh_token", data.refresh_token);
  },
  clearTokens: () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    sessionStorage.removeItem("access_token");
    sessionStorage.removeItem("refresh_token");
  },
  isAuthenticated: () =>
    !!localStorage.getItem("access_token") ||
    !!sessionStorage.getItem("access_token"),
};

const clearAuthSession = () => {
  tokenStorage.clearTokens();
  localStorage.removeItem("current_user");
  localStorage.removeItem("functionalities");
  localStorage.removeItem("factory_section");
  sessionStorage.removeItem("current_user");
};

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

const refreshToken = async (authApiUrl: string) => {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) throw new Error("Session expired. Please, Sign in.");

  try {
    const { data } = await axios.post<ITokenResponse>(
      `${authApiUrl}/auth/refresh`,
      { refreshToken },
      {
        headers: { "Content-Type": "application/json" },
      }
    );
    tokenStorage.setTokens(data);
    return data.data.access_token;
  } catch (error) {
    clearAuthSession();
    throw error;
  }
};

// Common interceptor logic
export const setupInterceptors = (client: any, baseUrl: string) => {
  // Keep track of the current page
  let currentPath = window.location.pathname;

  client.interceptors.request.use(
    (config: AxiosRequestConfig) => {
      if (config.url?.includes('amazonaws.com')) {
        return config;
      }
      
      const token = tokenStorage.getAccessToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error: AxiosError) => Promise.reject(error)
  );

  client.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as AxiosRequestConfig & {
        _retry?: boolean;
        _skipAuthRefresh?: boolean;
      };

      // Update current path
      currentPath = window.location.pathname;
      // Only try to refresh if:
      // 1. Got a 401 error
      // 2. Request hasn't been retried yet
      // 3. Not a login or refresh request
      // 4. We're not explicitly skipping auth refresh
      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        !originalRequest.url?.includes("/auth/login") &&
        !originalRequest.url?.includes("/auth/refresh") &&
        !originalRequest._skipAuthRefresh
      ) {
        originalRequest._retry = true;

        if (isRefreshing) {
          // Queue the request while refreshing
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              return client(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        isRefreshing = true;

        try {
          const newToken = await refreshToken(baseUrl);
          isRefreshing = false;
          processQueue(null, newToken);

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          return client(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          processQueue(refreshError);

          // Only redirect if we're not already on the signin page
          if (!currentPath.includes(LINKS.SIGNIN)) {
            // Clear tokens to ensure fresh login
            clearAuthSession();

            // More detailed error message based on the error type
            let errorMessage = "Session expired. Please sign in again.";

            if (refreshError instanceof Error) {
              errorMessage = refreshError.message;
            }

            toast.error(errorMessage);

            // Save the current URL to redirect back after login (optional)
            if (currentPath !== "/" && !currentPath.includes(LINKS.SIGNIN)) {
              sessionStorage.setItem(
                "redirectAfterLogin",
                window.location.pathname
              );
            }

            // Redirect to login page
            window.location.href = LINKS.SIGNIN;
          }

          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error.response?.data || error.message);
    }
  );
};
